import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, tVulnType } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SevBadge, VulnStatusBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, Plus, X, Link2 } from "lucide-react";

// Section réutilisable — vulnérabilités affectant un actif (fiche Actif).
export default function AssetVulnerabilities({ assetId, assetName }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const writable = can("vulnerability:write");
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("");

  const { data: vulns = [] } = useQuery({
    queryKey: ["asset-vulnerabilities", assetId],
    queryFn: async () => (await api.get(`/assets/${assetId}/vulnerabilities`)).data,
  });
  const { data: allVulns = [] } = useQuery({
    queryKey: ["vulnerabilities-min"],
    queryFn: async () => (await api.get("/vulnerabilities")).data,
    enabled: open,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["asset-vulnerabilities", assetId] });
    qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
  };

  const link = useMutation({
    mutationFn: async (vid) => api.post(`/vulnerabilities/${vid}/assets/${assetId}`),
    onSuccess: () => { toast.success("Vulnérabilité associée"); setOpen(false); setChoice(""); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const unlink = useMutation({
    mutationFn: async (vid) => api.delete(`/vulnerabilities/${vid}/assets/${assetId}`),
    onSuccess: () => { toast.success("Association retirée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const linkedIds = new Set(vulns.map((v) => v.id));
  const linkable = allVulns.filter((v) => !linkedIds.has(v.id));

  return (
    <section className="border border-border bg-card rounded-sm" data-testid="section-asset-vulnerabilities">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold">
          <ShieldAlert className="h-4 w-4 text-primary" /> Vulnérabilités — {vulns.length}
        </h2>
        {writable && (
          <Button size="sm" className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="link-vulnerability-button">
            <Plus className="h-4 w-4" /> Associer une vulnérabilité
          </Button>
        )}
      </div>
      <div className="p-6">
        <p className="text-xs text-muted-foreground mb-4">Vulnérabilités affectant cet actif (relation dédiée). La gravité est intrinsèque, ce n'est pas un risque.</p>
        {vulns.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune vulnérabilité associée.</p>
        ) : (
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead>Titre</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Gravité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>CVE</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vulns.map((v) => (
                  <TableRow key={v.id} className="border-border" data-testid={`asset-vuln-row-${v.id}`}>
                    <TableCell className="font-medium">
                      <button className="hover:text-primary text-left" onClick={() => navigate(`/vulnerabilities/${v.id}`)} data-testid={`asset-vuln-nav-${v.id}`}>{v.title}</button>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">{tVulnType(v.vulnerability_type)}</TableCell>
                    <TableCell><SevBadge value={v.severity} /></TableCell>
                    <TableCell><VulnStatusBadge value={v.status} /></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{v.cve_id || "—"}</TableCell>
                    <TableCell className="text-right">
                      {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" title="Retirer l'association" onClick={() => { if (window.confirm("Retirer cette association ?")) unlink.mutate(v.id); }} data-testid={`asset-vuln-unlink-${v.id}`}><X className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="link-vulnerability-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Associer une vulnérabilité</DialogTitle>
            <DialogDescription>À l'actif « {assetName} » (relation VULNERABILITY —AFFECTS→ ASSET).</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Vulnérabilité</Label>
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="link-vulnerability-select"><SelectValue placeholder="Choisir une vulnérabilité…" /></SelectTrigger>
              <SelectContent>
                {linkable.map((v) => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm gap-2" onClick={() => link.mutate(choice)} disabled={!choice || link.isPending} data-testid="confirm-link-vulnerability-button">
              <Link2 className="h-4 w-4" /> Associer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
